import { Observable, of, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DisclosedDocumentDto } from '@shared/models/disclosed-document-dto';
import { DocumentDetailsDto } from '@shared/models/document-details-dto';

@Injectable({
    providedIn: 'root'
})
export class DocumentService {

    constructor(private http: HttpClient) {
    }

    /**
     * Prepare the url of the document to be downloaded,
     * it supports downloading either from server or from form input.
     * There are two possible scenarios, the attachment may be retrieved either from db(backend)
     * or from the form input object(frontend).
     */
    downloadDisclosedDocument(attachment: DocumentDetailsDto, document: DisclosedDocumentDto, index = 0): Observable<{ url: string, title: string }> {
        const title = attachment.title;

        // dynamic url creation, documentId will be set in case of backend download
        if (document.fetchFromAres) {
            let url;
            if(attachment.documentId) {
                url = `api/portal/externalizedDocument/hrs/${attachment.hrsDocumentId}/${attachment.hrsItemId}/${document.isAdHocDocument}/download`;
            } else {
                url = URL.createObjectURL(attachment.binData);
            }

            return of({ url, title });
        }

        return this.getErsDocumentAttachmentsMetadata(document.ersDocumentId, document.isAdHocDocument)
            .pipe(switchMap((documentDetails: DocumentDetailsDto[]) => {
                const ersItemId = documentDetails[index]?.ersItemId;
                const url = `api/portal/externalizedDocument/ers/${document.ersDocumentId}/${ersItemId}/${document.isAdHocDocument}/preview`;

                return of({ url, title });
            }));
    }

    /**
     * Used for documents when we are not logged in
     * Filter document that have a documentId, push them to a list and send it to server
     */
    downloadDisclosedDocumentsAsZip(attachments: DocumentDetailsDto[], document: DisclosedDocumentDto): Observable<{ url: string }> {
        const url = document.fetchFromAres
            ? `api/portal/externalizedDocument/hrs/${attachments[0].hrsDocumentId}/download`
            : `api/portal/externalizedDocument/ers/${document.ersDocumentId}/${document.isAdHocDocument}/download`;
        return of({ url });
    }

    /**
     * Checks if doc, docx, pdf, odt or rtf documents have protection.
     * @param {File} attachment - The file.
     * @returns {Observable} - An Observable of the HTTP post request.
     */
    checkForPdfOrDocxProtection(attachment: File) {
        const formData = new FormData();
        formData.append('document', attachment, attachment.name);
        return this.http.post('api/portal/document/checkForPdfOrDocxProtection', formData);
    }

    /**
     * Fetches the attached documents metadata of DECIDE/OTHER document in
     * order to show this info to preview popup
     */
    getErsDocumentAttachmentsMetadata(ersDocumentId: string, isAdHocDocument: boolean): Observable<DocumentDetailsDto[]> {
        return this.http.get<DocumentDetailsDto[]>(`api/portal/externalizedDocument/ers/${ersDocumentId}/${isAdHocDocument}/searchErsDocument`);
    }
}
